import { faCircleExclamation, faInfoCircle, faWarning } from '@fortawesome/free-solid-svg-icons'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'

/** Os quatro estados possíveis do ícone de alerta da linha, em ordem de gravidade. */
export type TipoEstadoAlerta = 'erro' | 'inconsistencia' | 'calcario' | 'normal'

export interface EstadoAlerta {
  tipo: TipoEstadoAlerta
  icone: IconDefinition
  cor: string
  /** Rótulo exibido no topo do tooltip, para o usuário saber o que o ícone significa. */
  rotulo: string
  /** `true` quando o estado exige atenção — controla a largura do tooltip. */
  alertar: boolean
}

interface ParamsEstadoAlerta {
  resultado_inconsistente: number
  status?: number
  cod_calcario: number
  cod_calcario_propriedade: number
}

/**
 * Decide o ÚNICO estado da linha. Fonte de verdade compartilhada pelo ícone e pelo
 * tooltip — antes cada um recalculava por conta própria e os dois divergiam.
 *
 * Precedência: erro > inconsistência > calcário divergente > normal.
 */
export function obterEstadoAlerta({
  resultado_inconsistente,
  status,
  cod_calcario,
  cod_calcario_propriedade,
}: ParamsEstadoAlerta): EstadoAlerta {
  // Regra de produção: status 8 é RESULTADO COM ERRO.
  if (status === 8) {
    return {
      tipo: 'erro',
      icone: faCircleExclamation,
      cor: '#ff4d4f',
      rotulo: 'Resultado com erro',
      alertar: true,
    }
  }

  // Status 6/7 (INCONSISTÊNCIA MODERADA/ALTA) e a flag do backend alertam igual.
  if (resultado_inconsistente === 1 || status === 6 || status === 7) {
    return {
      tipo: 'inconsistencia',
      icone: faWarning,
      cor: '#fdd500',
      rotulo: 'Resultado com inconsistência',
      alertar: true,
    }
  }

  // `cod_calcario_propriedade` nulo significa propriedade SEM calcário cadastrado —
  // não é divergência. Esse caso já é sinalizado pelo botão bloqueado da coluna de ação.
  if (cod_calcario_propriedade !== null && cod_calcario !== cod_calcario_propriedade) {
    return {
      tipo: 'calcario',
      icone: faWarning,
      cor: '#f08e0d',
      rotulo: 'Calcário divergente',
      alertar: false,
    }
  }

  // Nada a sinalizar: ícone neutro, só para dar acesso a safra/laboratório no tooltip.
  return {
    tipo: 'normal',
    icone: faInfoCircle,
    cor: '#2586f5',
    rotulo: 'Sem alertas',
    alertar: false,
  }
}
